/* eslint-disable better-mutation/no-mutation */
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import getInitialState from './initial-state';
import type { GetPayoutSettingsModalViewModelApiResponse } from 'tradera/state/services/types/webapi-member-generated';
import type { PayoutDestinationModalState } from './types';

const payoutDestinationModalSlice = createSlice({
    name: 'payoutDestinationModal',
    initialState: getInitialState(),
    reducers: {
        receiveModalData: (
            state,
            {
                payload
            }: PayloadAction<GetPayoutSettingsModalViewModelApiResponse>
        ) => {
            state.requireBankIdVerification =
                payload.member?.requireBankIdVerification || false;
            state.tink.tinkLinkUrl =
                payload.tink?.tinkLinkUrl || state.tink.tinkLinkUrl;
            state.hyperwallet = {
                ...state.hyperwallet,
                ...payload.hyperwallet
            };
            state.availablePayoutOptions = payload.availablePayoutOptions || [];
            state.payoutFrequencyOption =
                payload.payoutFrequencyOption || undefined;
            state.isFetching = false;
        },
        setFetching: (state, { payload }) => {
            state.isFetching = payload;
        },
        resetPayoutDestinationOptionState: (state) => {
            const initialState = getInitialState();
            return {
                ...state,
                modalState: {
                    ...initialState.modalState,
                    ...{
                        selectedOption:
                            state.displayedPayoutOptions.length === 1
                                ? state.displayedPayoutOptions[0]
                                : undefined
                    }
                },
                tink: {
                    ...initialState.tink,
                    tinkLinkUrl: state.tink.tinkLinkUrl
                },
                bankAccount: initialState.bankAccount,
                paypal: initialState.paypal,
                swish: initialState.swish
            };
        },
        openSettingsModal: (state) => {
            state.showSettingsModal = true;
        },
        closeSettingsModal: (state) => {
            state.showSettingsModal = false;
        },
        setDisplayedPayoutOptions: (state, { payload }) => {
            state.displayedPayoutOptions = payload;
            if (payload.length === 1) {
                state.modalState = {
                    ...state.modalState,
                    selectedOption: payload[0]
                };
            }
        },
        // Tink
        setTinkFetching: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    isFetching: payload
                }
            };
        },
        setTinkBankAccounts: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    accounts: payload,
                    isFetching: false,
                    errorMessage: undefined
                }
            };
        },
        setTinkAccount: (state, { payload }: PayloadAction<string>) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    account: payload
                }
            };
        },
        tinkError: (state, { payload }) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    errorMessage: payload,
                    isFetching: false
                }
            };
        },
        cancelTink: (state) => {
            return {
                ...state,
                tink: {
                    ...state.tink,
                    accounts: null,
                    errorMessage: '',
                    isFetching: false
                }
            };
        },
        // Update status
        setUpdateStatus: (state, { payload }) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    ...payload
                }
            };
        },
        startUpdate: (state) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    isUpdating: true,
                    errorMessage: undefined,
                    processCorrelationId: undefined
                }
            };
        },
        setUpdateError: (state, { payload }) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    isUpdating: false,
                    errorMessage: payload
                },
                modalState: {
                    ...state.modalState
                }
            };
        },
        finishUpdate: (state) => {
            return {
                ...state,
                updateStatus: {
                    ...state.updateStatus,
                    isUpdating: false,
                    processCorrelationId: undefined
                }
            };
        },
        setModalState: (
            state,
            { payload }: PayloadAction<PayoutDestinationModalState>
        ) => {
            return {
                ...state,
                modalState: {
                    ...state.modalState,
                    ...payload
                }
            };
        },
        // Manual
        setBankAccount: (state, { payload }) => {
            return {
                ...state,
                bankAccount: {
                    ...state.bankAccount,
                    ...payload
                }
            };
        },
        // PayPal
        setPayPalEmail: (state, { payload }: PayloadAction<string>) => {
            return {
                ...state,
                paypal: {
                    ...state.paypal,
                    emailAddress: payload
                }
            };
        },
        // Swish
        setSwishNumber: (state, { payload }: PayloadAction<string>) => {
            return {
                ...state,
                swish: {
                    ...state.swish,
                    swishNumber: payload
                }
            };
        },
        setSwishVerificationCode: (
            state,
            { payload }: PayloadAction<string>
        ) => {
            return {
                ...state,
                swish: {
                    ...state.swish,
                    verificationCode: payload
                }
            };
        }
    }
});

export const {
    receiveModalData,
    setFetching,
    resetPayoutDestinationOptionState,
    openSettingsModal,
    closeSettingsModal,
    setDisplayedPayoutOptions,
    setTinkFetching,
    setTinkBankAccounts,
    setTinkAccount,
    tinkError,
    cancelTink,
    setUpdateStatus,
    startUpdate,
    setUpdateError,
    finishUpdate,
    setModalState,
    setBankAccount,
    setPayPalEmail,
    setSwishNumber,
    setSwishVerificationCode
} = payoutDestinationModalSlice.actions;

export default payoutDestinationModalSlice.reducer;
